import { NgClass, NgIf, DatePipe, SlicePipe, NgFor } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ChangeDetectorRef,
    inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { VenueService } from 'app/shared/services/venue.service';
import { Subject, takeUntil } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EditVenueDialogComponent } from './edit-venue-dialog/edit-venue-dialog.component';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
    selector: 'app-venues',
    templateUrl: './venues.component.html',
    styleUrl: './venues.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        NgClass,
        NgIf,
        NgFor,
        DatePipe,
        FormsModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatPaginatorModule,
        MatMenuModule,
        MatTooltipModule,
        MatDialogModule,
    ],
})
export class VenuesComponent implements OnInit, OnDestroy {
    data: any;
    recentVenuesDataSource: MatTableDataSource<any> = new MatTableDataSource();
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    
    // Search properties
    searchQuery: string = '';
    filteredVenues: any[] = [];
    showSearchDropdown: boolean = false;
    isSearchActive: boolean = false;
    selectedVenue: any = null;
    
    // Pagination properties
    pageSize: number = 10; // Show 10 items per page in table
    currentPage: number = 0;
    totalVenues: number = 0;
    pageSizeOptions: number[] = [10, 25, 50, 100];
    
    // Original venues from API
    allVenues: any[] = [];
    private readonly updatingFeaturedVenueIds = new Set<string>();
    
    // Stats properties
    activeVenuesCount: number = 0;
    inactiveVenuesCount: number = 0;
    
    // Expanded rows
    expandedVenueId: string | null = null;
    
    // Show all days for expanded view
    showAllDays: boolean = false;
    
    readonly dialog = inject(MatDialog);
    private readonly authService = inject(AuthService);
    readonly canPerformDestructiveActions = this.authService.isSuperAdmin();

    constructor(
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
        private venueService: VenueService,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
    ) { }

    ngOnInit(): void {
        this.getVenues();
    }

    getVenues(): void {
        this.venueService.getAllVenues({
            page: this.currentPage + 1,
            limit: this.pageSize,
            search: this.searchQuery
        })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    const venues = Array.isArray(response)
                        ? response
                        : response?.results || response?.data || [];

                    this.allVenues = venues.map(v => ({
                        id: v._id,
                        _id: v._id,
                        name: v.name,
                        location: v.location || {},
                        timing: v.timing || [],
                        status: v.status,
                        verified: v.verified,
                        onBoarded: v.onBoarded || false,
                        featured: v.featured === true,
                        cancellationPolicy: v.cancellationPolicy || null,
                        privacyPolicy: v.privacyPolicy || null,
                        rules: v.rules || null,
                        venuepic: v.venuepic || 'assets/icons/location.png',
                        companyId: v.companyId?._id || null,
                        companyName: v.companyId?.name || 'Unknown Company', // Get company name from companyId object
                        createdAt: v.createdAt,
                        updatedAt: v.updatedAt,
                        __v: v.__v
                    }));

                    this.calculateStats();
                    this.totalVenues = response?.totalResults || response?.total || this.allVenues.length;
                    this.filteredVenues = [...this.allVenues];
                    this.recentVenuesDataSource.data = [...this.filteredVenues];
                    this.showSearchDropdown = !!this.searchQuery.trim() && !this.selectedVenue && this.filteredVenues.length > 0;
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error fetching venues:', err);
                    this.snackBar.open('Error loading venues', 'Close', { duration: 3000 });
                    this.cdr.markForCheck();
                }
            });
    }

    // Calculate stats
    calculateStats(): void {
        this.activeVenuesCount = this.allVenues.filter(v => v.status === 'active').length;
        this.inactiveVenuesCount = this.allVenues.filter(v => v.status === 'inactive').length;
    }

    // Search functionality
    onSearchInput(): void {
        if (this.searchQuery.trim() === '') {
            this.clearSearch();
            return;
        }

        this.isSearchActive = true;
        this.selectedVenue = null;
        this.expandedVenueId = null;
        this.showAllDays = false;
        this.currentPage = 0;
        this.showSearchDropdown = false;

        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }

        this.searchDebounceTimer = setTimeout(() => {
            this.getVenues();
        }, 300);

        this.cdr.markForCheck();
    }

    selectVenue(venue: any): void {
        this.selectedVenue = venue;
        this.searchQuery = venue.name;
        this.showSearchDropdown = false;
        this.isSearchActive = true;
        this.expandedVenueId = venue._id;
        this.showAllDays = false;
        
        // Filter to show only selected venue
        this.filteredVenues = [venue];
        this.currentPage = 0;
        this.totalVenues = 1;
        this.recentVenuesDataSource.data = [...this.filteredVenues];
        
        // Scroll to the selected row
        setTimeout(() => {
            const element = document.getElementById(`venue-${venue._id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('bg-yellow-50');
                setTimeout(() => element.classList.remove('bg-yellow-50'), 2000);
            }
        }, 100);
        
        this.cdr.markForCheck();
    }

    clearSearch(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }

        this.searchQuery = '';
        this.selectedVenue = null;
        this.showSearchDropdown = false;
        this.isSearchActive = false;
        this.expandedVenueId = null;
        this.showAllDays = false;
        
        this.currentPage = 0;
        this.getVenues();
        
        this.cdr.markForCheck();
    }

    onSearchBlur(): void {
        setTimeout(() => {
            this.showSearchDropdown = false;
            this.cdr.markForCheck();
        }, 200);
    }

    // Helper methods for table display
    getOperatingDaysCount(venue: any): number {
        if (!venue.timing || !Array.isArray(venue.timing)) return 0;
        return venue.timing.filter((day: any) => day.checked).length;
    }

    getFirstOperatingTime(venue: any): string {
        if (!venue.timing || !Array.isArray(venue.timing) || venue.timing.length === 0) return '';
        
        const firstDay = venue.timing.find((day: any) => day.checked);
        if (firstDay) {
            return `${firstDay.name}: ${firstDay.startTime} - ${firstDay.endTime}`;
        }
        return 'No operating hours';
    }

    // Toggle row expansion
    toggleRow(venueId: string): void {
        if (this.expandedVenueId === venueId) {
            this.expandedVenueId = null;
            this.showAllDays = false;
        } else {
            this.expandedVenueId = venueId;
            this.showAllDays = false;
        }
        this.cdr.markForCheck();
    }

    // Toggle showing all days
    toggleShowAllDays(): void {
        this.showAllDays = !this.showAllDays;
        this.cdr.markForCheck();
    }

    // Get days to display based on showAllDays flag
    getDaysToDisplay(venue: any): any[] {
        if (!venue.timing || !Array.isArray(venue.timing)) return [];
        
        if (this.showAllDays || venue.timing.length <= 3) {
            return venue.timing;
        } else {
            return venue.timing.slice(0, 3);
        }
    }

    // Pagination methods
    updateDisplayedVenues(): void {
        this.recentVenuesDataSource.data = [...this.filteredVenues];
    }

    onPageChange(event: PageEvent): void {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        this.expandedVenueId = null;
        this.showAllDays = false;
        this.getVenues();
    }

    getPageInfo(): string {
        const start = (this.currentPage * this.pageSize) + 1;
        const end = Math.min((this.currentPage + 1) * this.pageSize, this.totalVenues);
        return `Showing ${start}-${end} of ${this.totalVenues} venues`;
    }

    refreshVenues(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }

        this.selectedVenue = null;
        this.showSearchDropdown = false;
        this.expandedVenueId = null;
        this.showAllDays = false;
        this.getVenues();
        this.snackBar.open('Venues refreshed', 'Close', { duration: 2000 });
    }

    // Add this method to toggle verification status
    toggleVerification(venueId: string, event: Event): void {
        event.stopPropagation(); // Prevent row expansion
        
        const venue = this.allVenues.find(v => v._id === venueId);
        if (!venue) return;
        
        const newStatus = !venue.verified;
        const confirmationMessage = newStatus 
            ? 'Are you sure you want to verify this venue?' 
            : 'Are you sure you want to unverify this venue?';
        
        // Show confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: newStatus ? 'Verify Venue' : 'Unverify Venue',
            message: confirmationMessage,
            actions: {
                confirm: {
                    label: newStatus ? 'Verify' : 'Unverify',
                    color: newStatus ? 'primary' : 'warn'
                },
                cancel: {
                    label: 'Cancel'
                }
            }
        });

        confirmation.afterClosed().subscribe(result => {
            if (result === 'confirmed') {
                this.venueService.verifyVenue(venueId, newStatus)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: (response) => {
                            // Update the venue in the local array
                            const index = this.allVenues.findIndex(v => v._id === venueId);
                            if (index !== -1) {
                                this.allVenues[index].verified = newStatus;
                                
                                // Update filtered venues if needed
                                const filteredIndex = this.filteredVenues.findIndex(v => v._id === venueId);
                                if (filteredIndex !== -1) {
                                    this.filteredVenues[filteredIndex].verified = newStatus;
                                }
                                
                                // Update the displayed data
                                this.updateDisplayedVenues();
                            }
                            
                            this.snackBar.open(
                                `Venue ${newStatus ? 'verified' : 'unverified'} successfully`, 
                                'Close', 
                                { duration: 3000 }
                            );
                            this.cdr.markForCheck();
                        },
                        error: (err) => {
                            console.error('Error updating verification status:', err);
                            this.snackBar.open('Error updating verification status', 'Close', { duration: 3000 });
                            this.cdr.markForCheck();
                        }
                    });
            }
        });
    }

    toggleFeatured(venueId: string, event: Event): void {
        event.stopPropagation();

        const venue = this.allVenues.find(v => v._id === venueId);
        if (!venue || this.updatingFeaturedVenueIds.has(venueId)) return;

        const featured = !venue.featured;
        const confirmation = this._fuseConfirmationService.open({
            title: featured ? 'Feature Venue' : 'Remove Featured Status',
            message: featured
                ? 'Feature this venue and prioritize it in customer venue listings?'
                : 'Remove featured priority from this venue?',
            actions: {
                confirm: {
                    label: featured ? 'Feature' : 'Remove',
                    color: featured ? 'primary' : 'warn'
                },
                cancel: { label: 'Cancel' }
            }
        });

        confirmation.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(result => {
                if (result !== 'confirmed') return;

                this.updatingFeaturedVenueIds.add(venueId);
                this.cdr.markForCheck();
                this.venueService.setVenueFeatured(venueId, featured)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: () => {
                            this.updateLocalFeaturedStatus(venueId, featured);
                            this.updatingFeaturedVenueIds.delete(venueId);
                            this.snackBar.open(
                                `Venue ${featured ? 'featured' : 'unfeatured'} successfully`,
                                'Close',
                                { duration: 3000 }
                            );
                            this.cdr.markForCheck();
                        },
                        error: (err) => {
                            this.updatingFeaturedVenueIds.delete(venueId);
                            console.error('Error updating featured status:', err);
                            this.snackBar.open(
                                err?.error?.message || 'Error updating featured status',
                                'Close',
                                { duration: 3000 }
                            );
                            this.cdr.markForCheck();
                        }
                    });
            });
    }

    isFeaturedUpdatePending(venueId: string): boolean {
        return this.updatingFeaturedVenueIds.has(venueId);
    }

    private updateLocalFeaturedStatus(venueId: string, featured: boolean): void {
        [this.allVenues, this.filteredVenues].forEach(venues => {
            const venue = venues.find(item => item._id === venueId);
            if (venue) venue.featured = featured;
        });
        this.updateDisplayedVenues();
    }

    // Add this method to handle quick verification from dropdown
    verifyVenueQuick(venueId: string, verify: boolean, event: Event): void {
        event.stopPropagation();
        
        this.venueService.verifyVenue(venueId, verify)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // Update the venue in the local array
                    const index = this.allVenues.findIndex(v => v._id === venueId);
                    if (index !== -1) {
                        this.allVenues[index].verified = verify;
                        
                        // Update filtered venues if needed
                        const filteredIndex = this.filteredVenues.findIndex(v => v._id === venueId);
                        if (filteredIndex !== -1) {
                            this.filteredVenues[filteredIndex].verified = verify;
                        }
                        
                        // Update the displayed data
                        this.updateDisplayedVenues();
                    }
                    
                    this.snackBar.open(
                        `Venue ${verify ? 'verified' : 'unverified'} successfully`, 
                        'Close', 
                        { duration: 3000 }
                    );
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error updating verification status:', err);
                    this.snackBar.open('Error updating verification status', 'Close', { duration: 3000 });
                    this.cdr.markForCheck();
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Track by function for ngFor loops
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * Open edit venue dialog
     */
    editVenue(venue: any, event: Event): void {
        event.stopPropagation(); // Prevent row expansion
        
        const dialogRef = this.dialog.open(EditVenueDialogComponent, {
            width: '1000px',
            maxWidth: '1000px',
            maxHeight: '90vh',
            data: { venue },
            disableClose: false,
            autoFocus: false,
            panelClass: 'edit-venue-dialog-container'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Refresh venues after successful update
                this.getVenues();
            }
        });
    }

    // NEW: Delete venue method using super admin API
    deleteVenue(venueId: string, venueName: string, event: Event): void {
        event.stopPropagation();
        
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Venue',
            message: `Are you sure you want to delete "${venueName}"? This action cannot be undone.`,
            actions: {
                confirm: {
                    label: 'Delete',
                    color: 'warn'
                },
                cancel: {
                    label: 'Cancel'
                }
            }
        });

        confirmation.afterClosed().subscribe(result => {
            if (result === 'confirmed') {
                this.venueService.deleteVenueBySuperAdmin(venueId)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: (response) => {
                            // Remove venue from local arrays
                            this.allVenues = this.allVenues.filter(v => v._id !== venueId);
                            this.filteredVenues = this.filteredVenues.filter(v => v._id !== venueId);
                            this.calculateStats();

                            const isLastVenueOnPage = this.filteredVenues.length === 0 && this.currentPage > 0;
                            if (isLastVenueOnPage) {
                                this.currentPage--;
                            }

                            this.getVenues();
                            
                            this.snackBar.open('Venue deleted successfully', 'Close', { duration: 3000 });
                            this.cdr.markForCheck();
                        },
                        error: (err) => {
                            console.error('Error deleting venue:', err);
                            const errorMessage = err?.error?.message || 'Error deleting venue';
                            this.snackBar.open(errorMessage, 'Close', { duration: 3000 });
                            this.cdr.markForCheck();
                        }
                    });
            }
        });
    }
}
